// Master Admin: connect Meta, choose which Facebook/Instagram Page(s) and
// lead forms feed leads into the OS, and see the webhook setup + recent events.
import React, { useCallback, useEffect, useState } from "react";
import { Link2, RefreshCw } from "lucide-react";
import { StatusPill, formatWhen, errorMessage } from "./integrationUi";

function PageRow({ page, api, showNotice, reload }) {
  const [forms, setForms] = useState(page.forms || []);
  const [selected, setSelected] = useState(page.form_ids || []);
  const [busy, setBusy] = useState(false);

  const loadForms = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/integrations/meta/leadforms/pages/${page.page_id}/forms`);
      setForms(data.forms);
    } catch (err) {
      showNotice(errorMessage(err, "Could not load lead forms"), "error");
    } finally {
      setBusy(false);
    }
  };

  const save = async (enabled) => {
    setBusy(true);
    try {
      await api.put(`/integrations/meta/leadforms/pages/${page.page_id}`, { enabled, form_ids: selected });
      showNotice(enabled ? "Page is receiving Lead Ads" : "Page disabled", "success");
      await reload();
    } catch (err) {
      showNotice(errorMessage(err, "Could not update Page"), "error");
    } finally {
      setBusy(false);
    }
  };

  const toggle = (id) => setSelected((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));

  return (
    <article className="provider-card" data-testid={`meta-page-${page.page_id}`}>
      <div>
        <strong>{page.page_name}</strong>
        <span>Page {page.page_id} - last lead: {formatWhen(page.last_lead_at)}</span>
        {page.last_error && <span style={{ color: "var(--destructive)" }} data-testid={`meta-page-error-${page.page_id}`}>{page.last_error}</span>}
      </div>
      <div className="provider-status">
        <StatusPill status={page.status === "needs_reauth" ? "needs_reauth" : page.enabled ? "connected" : "not_connected"} />
        <span>{page.enabled ? (page.subscribed ? "Receiving leads" : "Not subscribed") : "Disabled"}</span>
      </div>
      <div>
        <button className="ghost-btn small" data-testid={`meta-load-forms-${page.page_id}`} disabled={busy} onClick={loadForms}>Load lead forms</button>
        {forms.length > 0 && (
          <div className="masked-list" data-testid={`meta-forms-${page.page_id}`}>
            <span>{selected.length ? "Only the selected forms are imported:" : "No form selected - all forms on this Page are imported:"}</span>
            {forms.map((f) => (
              <label key={f.form_id} style={{ display: "flex", gap: 6 }}>
                <input type="checkbox" checked={selected.includes(f.form_id)} onChange={() => toggle(f.form_id)} /> {f.name}
              </label>
            ))}
          </div>
        )}
      </div>
      <div className="button-row">
        <button className="primary-btn small" data-testid={`meta-enable-${page.page_id}`} disabled={busy} onClick={() => save(true)}>
          {page.enabled ? "Save" : "Enable lead import"}
        </button>
        {page.enabled && <button className="ghost-btn small" data-testid={`meta-disable-${page.page_id}`} disabled={busy} onClick={() => save(false)}>Disable</button>}
      </div>
    </article>
  );
}

export default function MetaLeadAdsPanel({ api, showNotice }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/integrations/meta/leadforms");
      setData(res.data);
    } catch (err) {
      showNotice(errorMessage(err, "Could not load Meta Lead Ads"), "error");
    }
  }, [api, showNotice]);

  useEffect(() => {
    load();
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [load]);

  const connect = async () => {
    setBusy(true);
    try {
      const res = await api.post("/integrations/meta/authorize", { provider_type: "leadforms" });
      window.open(res.data.auth_url, "_blank", "noopener");
      showNotice("Meta consent opened in a new window. Come back here when you are done.", "info");
    } catch (err) {
      showNotice(errorMessage(err, "Could not start Meta connection"), "error");
    } finally {
      setBusy(false);
    }
  };

  const refreshPages = async () => {
    setBusy(true);
    try {
      const res = await api.post("/integrations/meta/leadforms/pages/refresh");
      showNotice(`Found ${res.data.pages_found} Page(s)`, "success");
      await load();
    } catch (err) {
      showNotice(errorMessage(err, "Could not refresh Pages"), "error");
    } finally {
      setBusy(false);
    }
  };

  if (!data) return <div className="panel glass-panel" data-testid="meta-leadads-panel">Loading Meta Lead Ads...</div>;
  const setupOk = data.app_configured && data.webhook_secret_configured && data.verify_token_configured;

  return (
    <div className="panel glass-panel" data-testid="meta-leadads-panel">
      <div className="panel-header">
        <h3>Meta Lead Ads (Facebook / Instagram)</h3>
        <StatusPill status={data.status} testId="meta-connection-status" />
      </div>
      <p style={{ fontSize: 13 }}>
        Leads from your Facebook and Instagram lead forms arrive here through a signed webhook and are added to the Lead Database
        (merged when the email or phone already exists), with a notification and an Inbox thread. Instagram/Facebook messaging is not connected.
      </p>

      <div className="compact-list" data-testid="meta-setup-checklist">
        <div className="compact-row"><div><strong>Meta app credentials</strong><span>{data.app_configured ? "Configured" : "Missing - set META_APP_ID and META_APP_SECRET"}</span></div></div>
        <div className="compact-row"><div><strong>Webhook signature secret</strong><span>{data.webhook_secret_configured ? "META_APP_SECRET set - unsigned requests are rejected" : "META_APP_SECRET not set - the webhook refuses every request (503)"}</span></div></div>
        <div className="compact-row"><div><strong>Webhook verify token</strong><span>{data.verify_token_configured ? "META_WEBHOOK_VERIFY_TOKEN set" : "META_WEBHOOK_VERIFY_TOKEN not set - Meta cannot verify the callback URL"}</span></div></div>
        <div className="compact-row"><div><strong>Callback URL for the Meta app (Webhooks - Page - leadgen)</strong>
          <code data-testid="meta-webhook-url">{data.webhook_url}</code></div></div>
      </div>

      <div className="button-row" style={{ margin: "12px 0" }}>
        <button className="primary-btn small" data-testid="meta-connect-button" disabled={busy || !data.app_configured} onClick={connect}>
          <Link2 size={14} /> {data.status === "connected" ? "Reconnect Meta" : data.status === "needs_reauth" ? "Reconnect Meta (access expired)" : "Connect Meta"}
        </button>
        {data.status === "connected" && (
          <button className="ghost-btn small" data-testid="meta-refresh-pages-button" disabled={busy} onClick={refreshPages}>
            <RefreshCw size={14} /> Refresh Pages
          </button>
        )}
      </div>
      {!setupOk && <p data-testid="meta-setup-incomplete" style={{ color: "var(--warning)" }}>Setup incomplete - leads will not arrive until every item above is done.</p>}

      <div className="provider-grid">
        {data.pages.map((p) => <PageRow key={p.page_id} page={p} api={api} showNotice={showNotice} reload={load} />)}
        {!data.pages.length && <div className="empty-cell">No Pages imported yet - connect Meta first.</div>}
      </div>

      <div className="panel-header" style={{ marginTop: 16 }}><h4>Recent lead events</h4><span>Last 20 webhook deliveries</span></div>
      <div className="compact-list" data-testid="meta-recent-events">
        {data.recent_events.map((e) => (
          <div className="compact-row" key={e.leadgen_id}>
            <div><strong>Lead {e.leadgen_id} - {e.status}{e.merged ? " (merged)" : ""}</strong>
              <span>{formatWhen(e.processed_at || e.received_at)}{e.error ? ` - ${e.error}` : ""}</span></div>
          </div>
        ))}
        {!data.recent_events.length && <div className="empty-cell">No leads received yet</div>}
      </div>
    </div>
  );
}
